import { sentencePaletteForPack } from '../components/sentenceMap/palette.js';
import { usePetPracticeReward } from '../teachersPet/usePetPracticeReward.js';
// src/pages/PackReaderSetText.jsx
//
// First student-facing content reader (set-text).
//   SLICE 1 (done) = FREE-ONLY: read /packs/{packId} (public) + getDocs(freeItems),
//     render each set_text via the verified pure-render core SetTextPreview.
//   SLICE 2 (this) = ENTITLEMENT-GATED PAID READ: additionally load getDocs(paidItems)
//     and render the paid set_text texts -- but ONLY for a user with an ACTIVE
//     entitlement for this pack. Non-buyers see the free texts + a generic
//     "buy to unlock" CTA. The paid read is STRICT-GATED: getDocs(paidItems) is never
//     attempted unless isEntitlementActive is true (rules also enforce this server-side
//     at firestore.rules:114-119; this is the client half). Audio is still Slice 3.
//
// GENERIC BY packId: the route is /set-text/:packId, so this works for ANY set-text pack
// (not hardcoded to the launch pack). Pure read -- no admin controls, no writes.
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, AUTH_MODE } from '../auth/firebase.js';
import { usePackItems } from '../admin/useAdminPacks.js';
import { useEntitlements } from '../storage/useEntitlements.js';
import { useIsAdmin } from '../auth/useIsAdmin.js';
import { useAuth } from '../auth/useAuth.js';
import { readerEntitled, adminBypassActive } from '../auth/adminView.js';
import { byDisplayOrder } from './readerGrouping.js';
import { AudioList } from '../components/ReaderAudio.jsx';
import SetTextPreview from '../admin/SetTextPreview.jsx';
import { AnnotationsContext, buildTermMatcher } from '../components/AnnotatedText.jsx';
import { selectPackAnnotations } from '../admin/packAnnotations.js';
import ReaderPageShell from './ReaderPageShell.jsx';
import ReaderAiDrawer from '../components/ReaderAiDrawer.jsx';
import ReaderNotesDrawer from '../components/ReaderNotesDrawer.jsx';
import ReaderItemActions from '../components/ReaderItemActions.jsx';
import JumpToAudioButton from '../components/JumpToAudioButton.jsx';
import StartHereCard from '../components/StartHereCard.jsx';
import { useBookmarks, userContentDocId } from '../storage/useUserContent.js';
import {
    selectSetTextItems, paidSectionState, selectAudioItems, partitionAudioByText,
    partitionFlashcards, flashcardDeck, showFlashcardLockedCta,
    partitionQuizzes, quizDeck, showQuizLockedCta, toQuizQuestions,
    selectEssayPrompts, partitionPracticeByText, practiceForText, selectGuideSections, resolveEssayEntry,
} from './setTextReaderGate.js';
import QuizQuestion from '../components/practice/QuizQuestion.jsx';
import PracticeFlashcards from '../components/practice/PracticeFlashcards.jsx';
import PracticeEssayView from '../components/practice/PracticeEssayView.jsx';
import ReadingTools from '../components/reader/ReadingTools.jsx';
import EssayGuideSection from './EssayGuideSection.jsx';
import { isAnswerComplete, isQuestionCorrect } from '../components/practice/practiceHelpers.js';
import { useProgress } from '../storage/useProgress.js';
import { POOLED_QUIZ_KEY, progressPercent } from '../storage/progressHelpers.js';
import { writeLastVisited, writePackStat } from '../storage/lastVisited.js';
import { IconCheck } from '../components/icons.jsx';
import { ItemTickButton, SectionCompleteToggle } from '../components/reader/ProgressControls.jsx';
import LessonBottomBar from '../components/reader/LessonBottomBar.jsx';
import SectionsSheet from '../components/reader/SectionsSheet.jsx';
import { flattenLessonOrder, prevNextFor } from '../components/reader/lessonMeta.js';
import LessonToast from '../components/reader/LessonToast.jsx';
import NextLessonCard from '../components/reader/NextLessonCard.jsx';
import SectionDrills from '../components/reader/SectionDrills.jsx';
import AskAiFab from '../components/reader/AskAiFab.jsx';
import { showAskAiFab } from '../components/reader/askAiFab.js';
import { withGuideIds } from '../components/reader/sectionDrills.js';
import { guidePracticeItems, textQuizItems, visibleGuide, guideReaderParams, guideReaderView } from './guidePractice.js';
import PracticeQuiz from '../components/practice/PracticeQuiz.jsx';
import { drillKey } from '../components/reader/SectionDrills.jsx';
import { nextContentEntry } from '../components/practice/feedbackState.js';
import { setTextLocation, setTextSelectionParams } from './setTextLocation.js';
import { useBetaContent } from '../analytics/useBetaContent.js';

export default function PackReaderSetText() {
    const { packId } = useParams();

    // Pack header (public read of /packs/{packId}).
    const [pack, setPack] = useState(null);
    const [packLoading, setPackLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        if (AUTH_MODE === 'mock' || !db || !packId) { setPack(null); setPackLoading(false); return undefined; }
        setPackLoading(true);
        getDoc(doc(db, 'packs', packId))
            .then((snap) => { if (!cancelled) { setPack(snap.exists() ? { id: snap.id, ...snap.data() } : null); setPackLoading(false); } })
            .catch((e) => { if (!cancelled) { console.error('PackReaderSetText pack:', e); setPack(null); setPackLoading(false); } });
        return () => { cancelled = true; };
    }, [packId]);

    // Free texts: always loaded (D2 -- the free read never waits on entitlement).
    const { items: freeItems, loading: freeLoading } = usePackItems(packId, 'freeItems');

    // Entitlement: auth-bound, read-only listener. `entitled` is only true once the
    // listener has RESOLVED (not entLoading) AND this pack has an active entitlement.
    const { activePackIds, loading: entLoading } = useEntitlements();
    // Admin read-view bypass: an admin may LOAD paid content without an entitlement
    // (firestore.rules already permits admin paidItems reads). Client VIEW only --
    // no entitlement record / purchase logic changes; tutors do NOT bypass. adminView.js.
    const realEntitlement = !entLoading && activePackIds.has(packId);
    const isAdmin = useIsAdmin();
    const entitled = readerEntitled({ realEntitlement, isAdmin });
    const showAdminView = adminBypassActive({ realEntitlement, isAdmin }); // admin viewing paid without an entitlement

    // Paid texts: STRICT-GATED on `entitled`. When entitled is false (incl. while
    // entitlement is still resolving), usePackItems is disabled and getDocs(paidItems)
    // is never called (D3) -- no attempt-then-deny.
    const { items: paidItems, loading: paidLoading, error: paidError, settled: paidSettled } =
        usePackItems(packId, 'paidItems', entitled);

    // Optional Tutor Review fee note (mirrors the literature reader) -- public/authed
    // read of the admin config doc; absent/denied -> null -> the panel shows a neutral
    // fallback. Powers the Tutor Review option inside the Essays practice view.
    const [tutorFeeNote, setTutorFeeNote] = useState(null);
    useEffect(() => {
        let cancelled = false;
        if (AUTH_MODE === 'mock' || !db) return undefined;
        getDoc(doc(db, 'config', 'tutor-review-settings'))
            .then((snap) => {
                const fee = snap.exists() && snap.data().content && snap.data().content.feeDescription;
                if (!cancelled) setTutorFeeNote(typeof fee === 'string' && fee.trim() ? fee.trim() : null);
            })
            .catch(() => { /* not configured / denied -> fallback copy */ });
        return () => { cancelled = true; };
    }, []);

    // D4: render only set_text items from either scope (audio/quiz/etc filtered out).
    const freeTexts = useMemo(() => selectSetTextItems(freeItems), [freeItems]);
    const paidTexts = useMemo(() => selectSetTextItems(paidItems), [paidItems]);

    const paidState = paidSectionState({
        entLoading,
        entitled,
        paidSettled,
        paidLoading,
        paidError,
        paidCount: paidTexts.length,
    });

    // Unified selection across free + (when ready) paid texts; the right pane renders
    // the selected text via the shared SetTextPreview core. Paid texts are only listed
    // when paidState === 'ready', so a selectable paid id can only exist for entitled users.
    const listedPaid = paidState === 'ready' ? paidTexts : [];
    // Combined cross-scope sequence (section-ordering S1): sorts only what is
    // already loaded; listedPaid stays gated on paidState === 'ready' above.
    const allTexts = useMemo(() => byDisplayOrder([...freeTexts, ...listedPaid]), [freeTexts, listedPaid]);
    // URL is the single selection source, including reloads and history POPs.
    // Resolve only against the visible list; pending/invalid IDs never grant access.
    const [searchParams, setSearchParams] = useSearchParams();
    const wantItem = searchParams.get('item');
    const { selected: sel, remember: rememberSelection } = setTextLocation(allTexts, wantItem, { freeLoading, paidState });
    const updateReaderLocation = patch => setSearchParams(previous => guideReaderParams(previous, patch), { replace: true });
    const selectText = (id, patch = {}) => {
        const next = setTextSelectionParams(searchParams, id, allTexts, patch);
        // Replace this reader's current entry, as view/guide switches already do:
        // Back still leaves the reader; Forward restores the last selected text.
        if (next) setSearchParams(next, { replace: true });
    };

    // Persisted progress (Scope A): manual completion ticks + quiz runs.
    const { completionMessage, completedSet, markItemComplete, unmarkItemComplete, recordQuizRun, quizState, saveQuizState, enabled: progressEnabled } = useProgress(packId, { articleRewards: true });
    // Manual progress (reader-manual-progress): opening a text no longer
    // auto-completes it -- the user ticks via the sidebar checkbox or the
    // "Mark complete" toggle in the content.
    // lesson-3b: the completion moment -- one toast at a time, nothing persisted.
    const [toast, setToast] = useState(null);
    const toggleItemComplete = useCallback(
        (itemId) => completedSet.has(itemId) ? unmarkItemComplete(itemId) : markItemComplete(itemId),
        [completedSet, markItemComplete, unmarkItemComplete]
    );

    // Audio (Slice 3): podcasts come from the SAME loaded scopes -- free audio from
    // freeItems (always), paid audio from paidItems (only loaded when entitled, so a
    // non-entitled session never receives it). Tag scope so the player knows whether it
    // must resolve a signed URL before playback.
    const freeAudio = useMemo(() => selectAudioItems(freeItems).map((a) => ({ ...a, _paid: false })), [freeItems]);
    const paidAudio = useMemo(() => selectAudioItems(paidItems).map((a) => ({ ...a, _paid: true })), [paidItems]);
    const visibleAudio = useMemo(() => [...freeAudio, ...paidAudio], [freeAudio, paidAudio]);
    // Route each podcast by relatedTextId against the texts the user can actually see;
    // unlinked / locked-target podcasts fall through to the pack-level section.
    const knownTextIds = useMemo(() => new Set(allTexts.map((t) => t.id)), [allTexts]);
    const { byText: audioByText, packLevel: packAudio } = useMemo(
        () => partitionAudioByText(visibleAudio, knownTextIds),
        [visibleAudio, knownTextIds],
    );

    // Pack glossary/annotations: public read of /config/terminology (the SAME doc the
    // Terminology page + grouped reader use). Best-effort; absent/denied -> no glossary.
    // Build the matcher ONCE (this pack's annotations first, then global terms) for the
    // in-reader popovers; AnnotatedText (consumed inside SetTextPreview) reads it via
    // context. Mirrors PackReaderGrouped verbatim -- additive, read-only, no schema change.
    const [glossaryTerms, setGlossaryTerms] = useState([]);
    useEffect(() => {
        let cancelled = false;
        if (AUTH_MODE === 'mock' || !db) return undefined;
        getDoc(doc(db, 'config', 'terminology'))
            .then((snap) => {
                const c = snap.exists() ? snap.data().content : null;
                const terms = (c && Array.isArray(c.terms)) ? c.terms : [];
                if (!cancelled) setGlossaryTerms(terms);
            })
            .catch(() => { /* not configured / denied -> no glossary */ });
        return () => { cancelled = true; };
    }, []);
    const termMatcher = useMemo(() => {
        const packAnns = selectPackAnnotations(glossaryTerms, packId);
        const globals = (glossaryTerms || []).filter((t) => t && t.scope !== 'pack');
        return buildTermMatcher([...packAnns, ...globals]);
    }, [glossaryTerms, packId]);

    // Flashcards (Slice: play view) -- pack-level cards from the SAME loaded scopes:
    // free always, paid only when entitled (paidItems is empty otherwise). No paid card
    // content can reach a non-entitled user (structural, like Slice 2/3).
    const { free: freeCards, paid: paidCards } = useMemo(
        () => partitionFlashcards(freeItems, paidItems),
        [freeItems, paidItems],
    );
    const deck = useMemo(
        () => flashcardDeck({ free: freeCards, paid: paidCards, entitled }),
        [freeCards, paidCards, entitled],
    );
    const cardsLockedCta = showFlashcardLockedCta({ entLoading, entitled, deckCount: deck.length });

    // Quizzes (Slice: MCQ play view) -- pooled pack-level, same gated scopes. Malformed
    // questions are skipped (Q6); no paid quiz content reaches a non-entitled user.
    const { free: freeQuizzes, paid: paidQuizzes } = useMemo(
        () => partitionQuizzes(freeItems, paidItems),
        [freeItems, paidItems],
    );
    // The RAW gated quiz items (free always; paid only when entitled). The pooled view maps
    // them to questions below; the per-section drill (settext-section-drills) takes the raw
    // items, exactly as the grouped readers do -- same gate, same shape, no new query.
    const quizPool = useMemo(
        () => quizDeck({ free: freeQuizzes, paid: paidQuizzes, entitled }),
        [freeQuizzes, paidQuizzes, entitled],
    );
    const quizQuestions = useMemo(() => toQuizQuestions(quizPool), [quizPool]);
    const quizLockedCta = showQuizLockedCta({ entLoading, entitled, deckCount: quizQuestions.length });

    // Essays (AI-graded practice) -- essay_prompt items from the SAME gated scopes:
    // free always, paid only when entitled (paidItems is empty otherwise, so no paid
    // prompt leaks). Mirrors the literature reader's essay practice exactly.
    const essayItems = useMemo(
        () => [...selectEssayPrompts(freeItems), ...selectEssayPrompts(paidItems)],
        [freeItems, paidItems],
    );
    // Locked hint: a resolved non-entitled user who can see at least one (free) essay.
    const essaysLockedCta = !entLoading && !entitled && essayItems.length > 0;

    // Essay guides: all sectionKind:'guide' entries from authorised scopes.
    // lit_section from the SAME visible scopes (free always; paid only when entitled).
    // No guides -> the Essays page renders exactly as today. Its presence also lets the
    // Essays tab appear for a guide-only pack (no essay prompts yet).
    const guides = useMemo(
        () => selectGuideSections([...freeItems, ...(entitled ? paidItems : [])]),
        [freeItems, paidItems, entitled],
    );
    const selectedGuideId = searchParams.get('guide');
    const setSelectedGuideId = id => updateReaderLocation({ guide: id });
    const guide = visibleGuide(guides, selectedGuideId);
    const hasEssaysTab = essayItems.length > 0 || !!guide;

    // Drill-only visibility set (guide-section-drills): knownTextIds + every visible guide id.
    // The guide is a lit_section, not a set_text, so it is NOT in knownTextIds -- and
    // must not be added there: knownTextIds also drives the audio / essay / pooled-practice
    // partitions, which would silently re-bucket. Only the two <SectionDrills> mounts read
    // this set. The pools passed with it are the SAME already-gated quizPool / deck.
    const drillKnownIds = useMemo(() => withGuideIds(knownTextIds, guides), [knownTextIds, guides]);
    const drillProps = {
        quizItems: quizPool, flashcardItems: deck, knownItemIds: drillKnownIds,
        showLockedCta: !entLoading && !entitled,
        recordQuizRun, persistEnabled: progressEnabled, quizState, saveQuizState,
    };

    // Progress (Scope A): completed texts / visible texts (drives the analysis-list bar).
    const textsDone = allTexts.reduce((n, t) => n + (completedSet.has(t.id) ? 1 : 0), 0);
    const textsPct = progressPercent(textsDone, allTexts.length);

    // "Continue where you left off" (library-2-shelf) -- see the note in
    // PackReaderGrouped. localStorage only, no Firestore, no schema.
    useEffect(() => {
        if (!packId || !sel || !rememberSelection) return;
        const ts = Date.now();
        // `title` is declared further down this component, so read the pack title from
        // `pack` (state, declared at the top) rather than referencing it before init.
        const packTitle = (pack && pack.metadata && pack.metadata.title) || packId || '';
        writeLastVisited({
            packId, itemId: sel.id,
            title: (sel.content && sel.content.title) || sel.title || '',
            packTitle,
            ts, done: textsDone, total: allTexts.length,
        });
        writePackStat(packId, { done: textsDone, total: allTexts.length, ts });
    }, [packId, sel, pack, textsDone, allTexts.length, rememberSelection]);

    // In-page view switch (F1): Analysis | Flashcards | Quizzes [| Essays], within /set-text/:packId.
    const view = guideReaderView(searchParams);
    useBetaContent(packId, sel?.id, view, rememberSelection);
    const setView = next => updateReaderLocation({ view: next });

    // Per-text practice grouping: by DEFAULT each practice view shows ONLY the currently
    // selected text's items + pack-level ("All texts") items (mirrors audio); other texts'
    // practice is hidden. The filter dropdown (in the practice views) lets the user switch
    // text or choose "All texts" (the full pool). practiceAllTexts=true -> show everything.
    const [practiceAllTexts, setPracticeAllTexts] = useState(false);
    const practiceTextId = practiceAllTexts ? null : (sel ? sel.id : null);
    const cardsByText = useMemo(() => partitionPracticeByText(deck, knownTextIds), [deck, knownTextIds]);
    const practiceGuideId = searchParams.get('quizGuide');
    const quizGuide = guides.find(g => g.id === practiceGuideId) || null;
    const guideQuizzes = useMemo(() => guidePracticeItems(quizPool, guides, quizGuide?.id), [quizPool, guides, quizGuide?.id]);
    const guideQuizCount = toQuizQuestions(guidePracticeItems(quizPool, guides, guide?.id)).length;
    const essaysByText = useMemo(() => partitionPracticeByText(essayItems, knownTextIds), [essayItems, knownTextIds]);
    const visibleDeck = practiceTextId == null ? deck : practiceForText(cardsByText, practiceTextId);
    const visibleQuiz = practiceTextId == null ? quizQuestions : textQuizItems(quizQuestions, knownTextIds, guides, practiceTextId);
    const visibleEssays = practiceTextId == null ? essayItems : practiceForText(essaysByText, practiceTextId);
    // AI Study Partner drawer (server enforces the free-exchange cap; entitled is a hint).
    const [aiOpen, setAiOpen] = useState(false);
    const [notesOpen, setNotesOpen] = useState(false);
    // The ONE open-the-drawer handler: the header pill and the floating button
    // (reader-ai-fab) both call it. Passing it to the shell is also the header's
    // render guard, so the FAB reads the same value to decide whether AI is offered.
    const [guideIsActive, setGuideIsActive] = useState(false);
    const guideReturnAnchor = useRef(null);
    const openGuideQuizzes = (id, anchor = 'guide-top') => {
        if (!guides.some(g => g.id === id)) return;
        updateReaderLocation({ guide: id, quizGuide: id, view: 'quizzes' });
        guideReturnAnchor.current = anchor;
        window.scrollTo(0, 0);
    };
    const returnToGuide = () => {
        if (!quizGuide) return;
        guideReturnAnchor.current ||= 'guide-top';
        updateReaderLocation({ guide: quizGuide.id, view: 'essays' });
    };
    useEffect(() => {
        if (view !== 'essays' || !guideReturnAnchor.current) return undefined;
        const frame = requestAnimationFrame(() => {
            document.getElementById(guideReturnAnchor.current)?.scrollIntoView({ block: 'start' });
            guideReturnAnchor.current = null;
        });
        return () => cancelAnimationFrame(frame);
    }, [view]);
    const switchView = (next) => {
        if (next === 'quizzes' && view === 'essays' && guideIsActive && guide) openGuideQuizzes(guide.id);
        else if (next === 'essays' && view === 'quizzes' && quizGuide) returnToGuide();
        else setView(next);
    };
    const openAi = useCallback(() => setAiOpen(true), []);
    // lesson-3a: below lg the text list lives in a bottom SHEET opened from the lesson
    // bar, not an inline disclosure. Selecting a text closes it.
    const [sheetOpen, setSheetOpen] = useState(false);
    const closeNav = useCallback(() => setSheetOpen(false), []);

    // N1-b: a deep-link from the Account "My Notes" list (?item=<id>&notes=1) auto-opens
    // the notes drawer once that text is actually selected, so the drawer targets it.
    const notesAutoOpened = useRef(false);
    useEffect(() => {
        if (notesAutoOpened.current || searchParams.get('notes') !== '1') return;
        if (!wantItem || (sel && sel.id === wantItem)) {
            setNotesOpen(true);
            notesAutoOpened.current = true;
        }
    }, [searchParams, wantItem, sel]);

    // Bookmarks (user-owned) for the selected text.
    const { bookmarkIds, toggleBookmark, uid: bmUid } = useBookmarks();
    const selTitle = (sel && sel.content && sel.content.title) || (sel && sel.title) || null;
    const selBookmarkId = sel ? userContentDocId(packId, sel.id) : null;

    // Jump-to-audio targets: the CURRENT text's inline audio (textAudioRef) and the
    // pack-level "More Podcasts" block (packAudioRef). The button shows whenever EITHER has
    // audio (hasAnyAudio) -- so a pack whose audio is all text-linked still gets a jump
    // affordance -- and scrolls to the text's audio first, else the pack-level block.
    const packAudioRef = useRef(null);
    const textAudioRef = useRef(null);
    const hasTextAudio = !!(sel && (audioByText[sel.id] || []).length > 0);
    const hasAnyAudio = hasTextAudio || packAudio.length > 0;
    const jumpToAudio = () => {
        const target = hasTextAudio ? textAudioRef.current : packAudioRef.current;
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (AUTH_MODE === 'mock' || !db) {
        return (
            <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-book-bg paper-texture">
                <div className="max-w-3xl mx-auto px-4 md:px-8 py-12">
                    <p className="font-serif italic text-book-muted">
                        The reader needs Firestore (emulator or firebase mode). It is unavailable in mock mode.
                    </p>
                </div>
            </div>
        );
    }

    const title = (pack && pack.metadata && pack.metadata.title) || packId;
    const subtitle = pack && pack.metadata && pack.metadata.subtitle;
    // Initial spinner only while we have nothing to show yet. Once free texts are in,
    // the page renders and the paid section resolves independently (D2).
    const initialLoading = (packLoading || freeLoading) && freeTexts.length === 0;

    // Admin read-view indicator (adminView.js) -- lesson-3a: a thin line under the top
    // bar rather than a band in the content column. Admin-only, unchanged in meaning.
    const adminBanner = showAdminView ? (
        <div className="border-b border-book-accent/30 bg-book-accent/5">
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-1.5 flex flex-wrap items-center gap-x-2">
                <span className="font-display text-book-accent text-xs font-semibold">Admin view</span>
                <span className="font-serif italic text-book-muted text-xs">Paid content shown without an entitlement.</span>
            </div>
        </div>
    ) : null;

    // lesson-3a: Prev/Next walk the visible texts in list order. The set-text reader has
    // ONE list (free then paid), so a single synthetic group is the whole nav order --
    // the same helper the grouped reader uses, so both readers step identically.
    const lessonOrder = flattenLessonOrder([{ key: 'texts', label: title, items: allTexts }], []);
    const { prev: prevEntry, next: nextEntry } = prevNextFor(lessonOrder, sel ? sel.id : null);
    const goEntry = (entry) => {
        if (!entry) return;
        selectText(entry.id);
        closeNav();
        window.scrollTo({ top: 0, behavior: 'auto' });
    };

    // lesson-3b: the next TEXT for the end-of-section card and the toast action.
    const nextLesson = sel ? nextContentEntry(lessonOrder, sel.id) : null;
    // Set-text content is not blocks-shaped, so the card is handed the normalised
    // content itself -- readingMinutes walks any object, so the estimate is the same
    // one the lesson header shows.
    const blocksFor = (id) => {
        const it = allTexts.find((t) => t.id === id);
        return (it && it.content) || null;
    };
    // Mark-complete raises a toast when it marks DONE; un-marking is silent, and the
    // sidebar ticks stay quiet (bookkeeping, not a completion moment).
    const markCompleteWithToast = async (itemId) => {
        const wasComplete = completedSet.has(itemId);
        const result = await toggleItemComplete(itemId);
        if (wasComplete) { setToast(null); return; }
        if (!result?.completed) return;
        setToast({
            id: itemId + ':' + Date.now(),
            message: nextLesson ? 'Section done · Next: ' + nextLesson.title : 'Section done',
            actionLabel: nextLesson ? 'Next' : null,
            next: nextLesson,
        });
    };

    // lesson-3a: one nav, rendered twice -- the desktop sidebar and, below lg, the
    // sections sheet. Free list + the entitlement-gated PaidSection are untouched.
    const navContent = (
        <>
        {/* "Start here" as a chip in the list rather than a band above the reading. */}
        <div className="px-1 pt-1">
            <StartHereCard packId={packId} startHere={pack && pack.startHere} />
        </div>
        {/* Progress (Scope A): completed texts / visible texts. */}
        {allTexts.length > 0 && (
            <div className="px-3 pt-2 pb-1">
                <div className="flex items-center justify-between mb-1">
                    <span className="font-display text-book-muted text-xs font-semibold">Progress</span>
                    <span className="font-display text-book-accent text-xs font-semibold">{textsDone} / {allTexts.length}</span>
                </div>
                <div className="h-1.5 bg-book-accent/10 rounded-full overflow-hidden">
                    <div className="h-full bg-book-accent transition-all duration-500" style={{ width: textsPct + '%' }} />
                </div>
            </div>
        )}
        <div className="font-display text-book-accent text-sm px-3 py-2 font-medium rounded-full">
            Free preview · {freeTexts.length} text{freeTexts.length === 1 ? '' : 's'}
        </div>
        {freeTexts.length === 0 ? (
            <p className="font-serif italic text-book-muted/70 text-[12px] px-3 pb-2">No free texts available yet.</p>
        ) : (
            freeTexts.map((t, i) => (
                <TextButton key={t.id} text={t} index={i} active={sel && sel.id === t.id} done={completedSet.has(t.id)}
                    canToggle={progressEnabled} onToggleDone={() => toggleItemComplete(t.id)}
                    onClick={() => { selectText(t.id); closeNav(); }} />
            ))
        )}

        {/* Paid section -- entitlement-gated (Slice 2). One of:
            resolving | locked | loading | error | empty | ready. */}
        <PaidSection
            state={paidState}
            texts={listedPaid}
            freeCount={freeTexts.length}
            sel={sel}
            onSelect={(id) => { selectText(id); closeNav(); }}
            completedSet={completedSet}
            progressEnabled={progressEnabled}
            onToggleItem={toggleItemComplete}
        />
        </>
    );
    return (
        <AnnotationsContext.Provider value={termMatcher}>
        <ReaderPageShell packTitle={title} onAskAi={openAi} adminBanner={adminBanner}>
            {/* lesson-3a: the pack header band is gone -- the title lives in the compact
                top bar, and each text opens with its own lesson header card. */}
            <div data-sentence-palette={sentencePaletteForPack(packId)} className="max-w-6xl mx-auto px-4 md:px-8 py-5 md:py-10 btl-lesson-scrollpad">
                {subtitle && (
                    <p className="font-serif italic text-book-muted text-sm mb-4 lg:hidden">{subtitle}</p>
                )}

                {initialLoading ? (
                    <p className="font-serif italic text-book-muted">Loading…</p>
                ) : (
                  <>
                    <ViewToggle view={view} setView={switchView} showEssays={hasEssaysTab} />

                    {/* Per-text practice filter (only when the pack has >1 text -- otherwise
                        everything is this-text-or-pack-level anyway). Default follows the
                        selected text; "All texts" shows the full pool. */}
                    {view !== 'analysis' && (allTexts.length > 1 || (view === 'quizzes' && guides.length > 0)) && (
                        <div className="flex min-w-0 items-center gap-2 mb-4">
                            <label htmlFor="practice-scope" className="shrink-0 font-display text-book-muted text-xs font-semibold">Practice for</label>
                            <select
                                id="practice-scope"
                                value={view === 'quizzes' && quizGuide ? 'guide:' + quizGuide.id : (practiceAllTexts ? 'all' : (sel ? sel.id : 'all'))}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v.startsWith('guide:')) {
                                        const id = v.slice(6);
                                        if (guides.some(g => g.id === id)) { updateReaderLocation({ guide: id, quizGuide: id }); guideReturnAnchor.current = 'guide-top'; }
                                    } else {
                                        if (v === 'all') { setPracticeAllTexts(true); updateReaderLocation({ quizGuide: null }); }
                                        else { setPracticeAllTexts(false); selectText(v, { quizGuide: null }); }
                                    }
                                }}
                                className="min-w-0 w-full max-w-full flex-1 bg-book-bg border border-book-accent/30 px-3 py-1.5 text-book-text font-serif text-sm focus:outline-none focus:border-book-accent">
                                <option value="all">All texts</option>
                                {allTexts.map((t) => (
                                    <option key={t.id} value={t.id}>{(t.content && t.content.title) || t.id}</option>
                                ))}
                                {view === 'quizzes' && guides.length > 0 && <optgroup label="Essay Guides">
                                    {guides.map(g => <option key={g.id} value={'guide:' + g.id}>{g.content?.title || 'Essay Guide'}</option>)}
                                </optgroup>}
                            </select>
                        </div>
                    )}

                    {view === 'flashcards' ? (
                        <div className="analysis-content"><PracticeFlashcards deck={visibleDeck} showCta={cardsLockedCta} title={title} /></div>
                    ) : view === 'quizzes' ? (
                        quizGuide ? <GuideQuizPanel key={quizGuide.id} guide={quizGuide} items={guideQuizzes}
                            onReturn={returnToGuide} persistEnabled={progressEnabled}
                            onComplete={r => recordQuizRun(drillKey(quizGuide.id), r)}
                            savedQuizState={quizState?.[drillKey(quizGuide.id)]}
                            onSaveProgress={blob => saveQuizState(drillKey(quizGuide.id), blob)} />
                        : <div className="analysis-content"><QuizView questions={visibleQuiz} showCta={quizLockedCta} title={title} persistEnabled={progressEnabled} onComplete={(r) => recordQuizRun(POOLED_QUIZ_KEY, r)} /></div>
                    ) : view === 'essays' && hasEssaysTab ? (
                        /* Essays render OUTSIDE .analysis-content (interactive textarea), mirroring literature. */
                        <EssaysView key={practiceAllTexts ? 'all' : sel?.id} essays={visibleEssays} guide={guide} guides={guides} onSelectGuide={setSelectedGuideId} onOpenQuizzes={openGuideQuizzes} quizCount={guideQuizCount} packId={packId} entitled={entitled} tutorFeeNote={tutorFeeNote} showCta={essaysLockedCta}
                            drills={drillProps} onGuideChange={setGuideIsActive} availableAudios={visibleAudio} />
                    ) : (
                      <>
                        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
                        {/* LEFT: text list (free always; paid when entitled) + paid-section state.
                            lesson-3a: hidden below lg -- the same navContent renders in the sheet. */}
                        <aside className="hidden lg:block rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-2 h-fit lg:sticky lg:top-16 max-h-[78vh] overflow-y-auto custom-scrollbar">
                            {navContent}
                        </aside>

                        {/* RIGHT: per-item actions (bookmark + Notes -- chrome, OUTSIDE
                            .analysis-content) + the selected text + its inline podcasts.
                            .analysis-content (ADR-009); the left text-list nav is NOT. */}
                        <div>
                            {bmUid && sel && (
                                <ReaderItemActions
                                    isBookmarked={bookmarkIds.has(selBookmarkId)}
                                    onToggleBookmark={() => toggleBookmark({ packId, itemId: sel.id, itemType: 'set_text', title: selTitle })}
                                    onOpenNotes={() => setNotesOpen(true)} />
                            )}
            {!initialLoading && view === 'analysis' && !aiOpen && !notesOpen && allTexts.length > 0 && (
                <LessonBottomBar
                    onPrev={prevEntry ? () => goEntry(prevEntry) : null}
                    onNext={nextEntry ? () => goEntry(nextEntry) : null}
                    prevLabel={prevEntry && prevEntry.title}
                    nextLabel={nextEntry && nextEntry.title}
                    complete={!!(sel && completedSet.has(sel.id))}
                    showComplete={!!(progressEnabled && sel)}
                    onToggleComplete={() => sel && markCompleteWithToast(sel.id)}
                    onOpenSections={() => setSheetOpen(true)} />
            )}
                            {/* Manual "Mark complete" toggle for the current text -- in sync
                                with the sidebar tick (same completedSet). */}
                            {progressEnabled && sel && (
                                <div className="mb-4 hidden lg:block">
                                    <SectionCompleteToggle complete={completedSet.has(sel.id)}
                                        onToggle={() => markCompleteWithToast(sel.id)} />
                                </div>
                            )}
                            {/* Below the Save/Notes group: jump to audio reachable from this
                                view -- the current text's inline audio or the pack-level
                                podcasts. Auto-hidden when the view has no audio at all. */}
                            {hasAnyAudio && <JumpToAudioButton onJump={jumpToAudio} />}
                            {completionMessage && <p role="status" className="mb-3 text-sm text-book-muted">{completionMessage}</p>}
                            <div className="analysis-content">
                            {sel ? (
                                <>
                                    <SetTextPreview content={sel.content} eyebrow="Set text" done={completedSet.has(sel.id)} />
                                    {/* Per-section "Drill it" (settext-section-drills): the quizzes +
                                        flashcards Danielle linked to THIS text, between the text and
                                        its podcasts -- the same shared block the grouped readers show.
                                        Items are the already-gated pools (free always; paid only when
                                        entitled); drillKnownIds = the audio visibility gate + the guide.
                                        Nothing renders for a text with no linked practice. Practice
                                        views replace this whole pane, so the block is never shown
                                        during a pooled session. */}
                                    <SectionDrills section={sel} {...drillProps} />
                                    <div ref={textAudioRef}>
                                        <AudioList heading="Analysis Podcasts" audios={audioByText[sel.id] || []} packId={packId} />
                                    </div>
                                    {/* lesson-3b: close the loop -- the next text, or the
                                        fact that this was the last one. */}
                                    <NextLessonCard next={nextLesson} blocksFor={blocksFor} onGo={goEntry} />
                                </>
                            ) : (
                                <p className="font-serif italic text-book-muted">Select a text from the list.</p>
                            )}
                            </div>
                        </div>
                    </div>

                        {/* Pack-level podcasts: not linked to a specific text (or linked
                            to a text this user can't see). Nothing is lost (S1). */}
                        <div ref={packAudioRef}>
                            <AudioList heading="More Podcasts" audios={packAudio} packId={packId} />
                        </div>
                      </>
                    )}
                  </>
                )}
            </div>

            {/* AI Study Partner drawer -- portals to document.body (escapes the shell's
                lingering page-fade-in transform) but stays a React child of the shell so
                it reads the live day/night skin from ReadingPrefsContext. */}
            <ReaderAiDrawer
                open={aiOpen}
                onClose={() => setAiOpen(false)}
                packId={packId}
                packTitle={title}
                entitled={entitled}
                currentItemId={view === 'essays' && guideIsActive ? guide?.id : sel?.id}
                currentItemType={view === 'essays' && guideIsActive ? 'lit_section' : 'set_text'}
                currentItemTitle={view === 'essays' && guideIsActive ? guide?.content?.title : selTitle} />

            {/* My Notes drawer (user-owned, .selectable so content-protection won't block typing). */}
            <ReaderNotesDrawer
                open={notesOpen}
                onClose={() => setNotesOpen(false)}
                packId={packId}
                itemId={sel ? sel.id : null}
                itemType="set_text"
                title={selTitle} />

            {/* lesson-3a mobile lesson bar + sections sheet. The bar is hidden on every
                practice view (`view !== 'analysis'` covers flashcards / quizzes / essays)
                and while a drawer is open, so it can never sit over the essay textarea,
                its Submit, or a drawer's controls. */}
            <SectionsSheet open={sheetOpen} onClose={closeNav} title="Texts">
                {navContent}
            </SectionsSheet>
            {/* Floating Ask AI (reader-ai-fab). Same hide list as the lesson bar above --
                this reader's "no practice session" is view === 'analysis' -- plus the
                open sheet and the header's own guard (openAi is what the shell renders on). */}
            {showAskAiFab({ canAsk: openAi, initialLoading, aiOpen, notesOpen, inPractice: view !== 'analysis' && !(view === 'essays' && guideIsActive), sheetOpen }) && (
                <AskAiFab onClick={openAi} packId={packId} itemId={view === "essays" && guideIsActive ? guide?.id : sel?.id} />
            )}
            <LessonToast toast={toast} onDismiss={() => setToast(null)}
                onAction={() => { const n = toast && toast.next; setToast(null); if (n) goEntry(n); }} />
        </ReaderPageShell>
        </AnnotationsContext.Provider>
    );
}

// A single selectable text row (shared by the free + paid lists).
function TextButton({ text, index, active, onClick, done, onToggleDone, canToggle }) {
    const tTitle = (text.content && text.content.title) || text.id;
    return (
        <button
            onClick={onClick}
            className={`w-full text-left px-3 py-2 mb-1 flex items-start gap-2 border-l-2 transition-colors ${active ? 'bg-book-accent/10 border-book-accent' : 'border-transparent hover:bg-book-accent/5'}`}
        >
            {/* Left tick toggle (manual progress); stopPropagation lives in the
                ItemTickButton so ticking does not also open the text. The "01"
                numbering stays. */}
            {canToggle && <span className="mt-0.5"><ItemTickButton complete={done} onToggle={onToggleDone} label={tTitle} /></span>}
            <span className="font-display text-xs text-book-accent mt-0.5 font-semibold">{String(index + 1).padStart(2, '0')}</span>
            <span className={`font-serif text-sm leading-tight flex-grow ${active ? 'text-book-text' : 'text-book-muted'}`}>{tTitle}</span>
            {!canToggle && done && <IconCheck className="w-3 h-3 text-book-accent flex-shrink-0 mt-1" />}
        </button>
    );
}

// The entitlement-gated paid area of the sidebar. Pure presentation of paidState.
function PaidSection({ state, texts, freeCount, sel, onSelect, completedSet, progressEnabled, onToggleItem }) {
    if (state === 'resolving') {
        return (
            <div className="mt-3 mx-2 border border-book-accent/15 bg-book-bg/30 p-3 text-center">
                <p className="font-serif italic text-book-muted/70 text-xs font-semibold">Checking your access…</p>
            </div>
        );
    }
    if (state === 'locked') {
        // D1: generic "buy to unlock" CTA. No paid titles (no manifest in v1).
        // L2a-2 NOTE: if a paid-items manifest is ever built (to tease locked
        // content to non-buyers), it MUST EXCLUDE essay_prompt items (or use a
        // neutral title) -- their prompt text / sample essay can leak the exercise.
        return (
            <div className="mt-3 mx-2 border border-book-accent/20 bg-book-bg/40 p-3 text-center">
                <div className="font-display text-book-muted/80 text-xs mb-1 font-semibold">🔒 More with purchase</div>
                <p className="font-serif italic text-book-muted/70 text-xs leading-snug font-semibold">
                    Full analysis, the complete text set and podcast audio unlock with the pack.
                </p>
            </div>
        );
    }
    if (state === 'loading') {
        return (
            <div className="mt-3 mx-2 border border-book-accent/15 bg-book-bg/30 p-3 text-center">
                <p className="font-serif italic text-book-muted/70 text-xs font-semibold">Loading your texts…</p>
            </div>
        );
    }
    if (state === 'error') {
        // D5: inline error only -- the free read above is unaffected.
        return (
            <div className="mt-3 mx-2 border border-red-400/30 bg-red-900/10 p-3 text-center">
                <p className="font-serif italic text-red-300/80 text-xs leading-snug font-semibold">
                    We couldn’t load your purchased texts. Please refresh to try again.
                </p>
            </div>
        );
    }
    if (state === 'empty') {
        // D4: entitled but the pack has no paid set_text items yet -- graceful, no CTA.
        return (
            <div className="mt-3 mx-2 border border-book-accent/15 bg-book-bg/30 p-3 text-center">
                <p className="font-serif italic text-book-muted/70 text-xs font-semibold">No additional texts in this pack yet.</p>
            </div>
        );
    }
    // 'ready'
    return (
        <>
            <div className="font-display text-book-accent text-sm px-3 py-2 mt-2 border-t border-book-accent/15 font-medium rounded-full">
                Your texts · {texts.length} text{texts.length === 1 ? '' : 's'}
            </div>
            {texts.map((t, i) => (
                <TextButton key={t.id} text={t} index={freeCount + i} active={sel && sel.id === t.id} done={completedSet && completedSet.has(t.id)}
                    canToggle={progressEnabled} onToggleDone={() => onToggleItem(t.id)}
                    onClick={() => onSelect(t.id)} />
            ))}
        </>
    );
}

// ---- In-page view switch + Flashcards play view ----

// Analysis | Flash Cards | Quizzes toggle (ported from the prototype view-tabs,
// L14338-14351). Stays within /set-text/:packId; defaults to Analysis.
export function ViewToggle({ view, setView, showEssays }) {
    const views = [
        { id: 'analysis', label: 'Analysis', Icon: IconBookOpen },
        { id: 'flashcards', label: 'Flash Cards', Icon: IconCards },
        { id: 'quizzes', label: 'Quizzes', Icon: IconQuestion },
        // Essays tab only when the pack has essay_prompt items (avoid an empty view).
        ...(showEssays ? [{ id: 'essays', label: 'Essays', Icon: IconPencil }] : []),
    ];
    return (
        <div className="mb-6 max-w-full overflow-x-auto">
            <div className="flex w-max mx-auto items-center bg-book-pageDark border border-book-accent/20 rounded-sm">
                {views.map((v) => (
                    <button key={v.id} onClick={() => setView(v.id)}
                        className={`shrink-0 whitespace-nowrap px-4 py-2 font-display text-sm flex items-center gap-1.5 transition-colors font-semibold ${
                            view === v.id ? 'bg-book-accent text-black' : 'text-book-muted hover:text-book-accent'
                        }`}>
                        <v.Icon /> <span>{v.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

// Essays practice view: an optional pack-level GUIDE entry FIRST (essay-structure-
// guide), then the pack's essay_prompt items by title (free + paid, already gated).
// A practice prompt mounts PracticeEssayView (timed essay + AI gradeEssay + Tutor
// Review); the guide renders EssayGuideSection INSTEAD (no timer/submit/grading).
// Selecting the guide UNMOUNTS PracticeEssayView, so its countdown interval is
// cleaned up -- no running timer or essay state leaks across the switch. In-memory
// only; no persistence. `essays` may be empty when the pack has only a guide.
// `drills` (guide-section-drills): the parent's already-gated SectionDrills props, so the
// guide page can carry the quizzes / flashcards Danielle linked to the guide. Threaded
// down, never re-derived here.
export function EssaysView({ essays, guide, guides = guide ? [guide] : [], onSelectGuide, onOpenQuizzes, quizCount = 0, packId, entitled, tutorFeeNote, showCta, drills, onGuideChange, availableAudios = [] }) {
    const { currentUser } = useAuth();
    const missionOwner = JSON.stringify([currentUser?.uid || null, packId]);
    const [missionState, setMissionState] = useState({ owner: missionOwner, values: {} });
    const missionValues = missionState.owner === missionOwner ? missionState.values : {};
    // Begin with the pack's structure guide whenever this Essays view opens.
    const defaultKey = guide?.id || essays[0]?.id || null;
    const [selKey, setSelKey] = useState(null);
    const activeKey = selKey === guide?.id || essays.some(e => e.id === selKey) ? selKey : defaultKey;

    // Guide-vs-prompt decision (pure, unit-tested): keeps the always-on guide
    // reachable even if the practice filter empties the essay list under a stale
    // selKey. onGuide -> render the guide; else `sel` is the essay to render.
    const { onGuide, sel } = resolveEssayEntry(essays, guide, activeKey);
    useEffect(() => { onGuideChange?.(onGuide); return () => onGuideChange?.(false); }, [onGuide, onGuideChange]);

    if (!onGuide && !sel) {
        return (
            <div className="max-w-3xl mx-auto text-center py-20">
                <p className="font-serif italic text-book-muted text-lg">No essay practice available for this pack yet.</p>
            </div>
        );
    }
    return (
        <div id="guide-top" className="max-w-4xl mx-auto pb-8 scroll-mt-24">
            {guide && <div className="mb-5 rounded-2xl border-2 border-book-accent/40 bg-book-accent/5 p-5">
                <p className="text-xs font-display text-book-accent mb-2">STEP 1 · START HERE</p>
                {guides.length > 1 && <label className="block font-display text-sm text-book-muted mb-3">Essay Guide
                    <select aria-label="Essay Guide" value={guide.id} onChange={e => { onSelectGuide?.(e.target.value); setSelKey(e.target.value); }}
                        className="block w-full min-w-0 mt-1 rounded-lg border border-book-accent/30 bg-book-bg text-book-text p-2">
                        {guides.map(g => <option key={g.id} value={g.id}>{g.content?.title || 'Essay Guide'}</option>)}
                    </select>
                </label>}
                <button onClick={() => setSelKey(guide.id)} aria-pressed={onGuide} className="w-full flex flex-col items-start sm:flex-row sm:items-center justify-between gap-3 text-left text-lg font-display font-semibold text-book-accent border-2 border-book-accent rounded-xl px-4 py-3 hover:bg-book-accent/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"><span className="flex min-w-0 items-center gap-2"><IconBookOpen /> {guide.content?.title || 'Essay Structure 101'}</span><span className="text-sm underline whitespace-nowrap">{onGuide ? 'Reading guide ✓' : 'Open guide →'}</span></button>
                <p className="mt-2 text-sm text-book-muted">Learn how to structure your answer before moving on to a practice question.</p>
            </div>}
            {(guide || essays.length > 1) && (
                <div className="mb-6">
                    {guide && essays.length > 0 && <p className="text-xs font-display text-book-muted mb-3">STEP 2 · PRACTISE YOUR ANSWER</p>}
                    <div className="flex flex-wrap gap-2 justify-center">
                        {essays.map((e) => {
                            const active = !onGuide && sel && sel.id === e.id;
                            return (
                                <button key={e.id} onClick={() => setSelKey(e.id)}
                                    className={`px-4 py-2 font-display text-sm border transition-colors font-semibold ${active ? 'bg-book-accent text-black border-book-accent' : 'border-book-accent/30 text-book-muted hover:text-book-accent'}`}>
                                    {e.title || 'Timed Essay'}
                                </button>
                            );
                        })}
                    </div>

                </div>
            )}

            {onGuide ? (
                <>
                    <ReadingTools key={guide.id} packId={packId} itemId={guide.id} />
                    {onOpenQuizzes && quizCount > 0 && <button className="mb-4 px-4 py-2 rounded-xl border border-book-accent text-book-accent font-display text-sm"
                        onClick={() => onOpenQuizzes(guide.id)}>Open this guide's quizzes ({quizCount}) →</button>}
                    <div className="analysis-content"><EssayGuideSection key={missionOwner + '/' + guide.id} content={guide.content} quizCount={quizCount}
                        lessonOptions={{ packId, availableAudios, state: missionValues[guide.id] || {}, onChange: value => setMissionState(previous => ({ owner: missionOwner, values: { ...(previous.owner === missionOwner ? previous.values : {}), [guide.id]: value } })) }}
                        onOpenQuizzes={onOpenQuizzes ? anchor => onOpenQuizzes(guide.id, anchor) : undefined} /></div>
                    {/* Drill It on the GUIDE (guide-section-drills): the same shared block as a
                        set text. The guide is a lit_section, so its id lives only in the
                        parent's drill-only id set. Nothing renders when nothing is linked. */}
                    {drills && <SectionDrills section={guide} {...drills} />}
                    {essays[0] && <button className="mt-6 px-5 py-3 rounded-full bg-book-accent text-book-pageDark font-display" onClick={() => setSelKey(essays[0].id)}>Continue to practice →</button>}
                </>
            ) : (
                <PracticeEssayView item={sel} packId={packId} entitled={entitled} tutorFeeNote={tutorFeeNote} />
            )}

            {showCta && !onGuide && (
                <div className="mt-6 max-w-md mx-auto border border-book-accent/20 bg-book-bg/40 p-3 text-center">
                    <div className="font-display text-book-muted/80 text-xs mb-1 font-semibold">🔒 More with purchase</div>
                    <p className="font-serif italic text-book-muted/70 text-xs leading-snug font-semibold">More essay practice unlocks with the pack.</p>
                </div>
            )}
        </div>
    );
}

// Quizzes MCQ runner (ported from the prototype SetTextQuizView, L15015-15135): answer a
// question -> submit -> review (correct/wrong + explanation) -> next -> completion score.
// Saves the latest result after each submission, including unfinished runs.
// Answer widgets remain in-session; questions are already gated + malformed-skipped.
export function QuizView({ questions, showCta, title, onComplete, persistEnabled }) {
    const { reward: petReward, checkReward } = usePetPracticeReward();
    const rawAnswers = useRef({});
    const [idx, setIdx] = useState(0);
    const [syncStatus, setSyncStatus] = useState(''), [syncError, setSyncError] = useState(false);
    const lastReport = useRef(null);
    const syncReport = async () => {
        if (!lastReport.current) return;
        setSyncStatus('Saving answers…'); setSyncError(false);
        try { await onComplete?.(lastReport.current); setSyncStatus(persistEnabled ? 'Results saved to your learning report.' : 'Sign in to save your progress.'); }
        catch { setSyncStatus('Could not save answers. Check your connection, then retry.'); setSyncError(true); }
    };
    const [answer, setAnswer] = useState(null); // generic: mcq index | fill string | matching map
    const [showResult, setShowResult] = useState(false);
    const [score, setScore] = useState(0);
    const [completed, setCompleted] = useState(false);

    // Reset if the question set changes (e.g. paid quizzes arrive after entitlement resolves).
    useEffect(() => {
        setIdx(0); setAnswer(null); setShowResult(false); setScore(0); setCompleted(false); setSyncStatus(''); setSyncError(false); lastReport.current = null; rawAnswers.current = {};
    }, [questions.map(q => q.id).join('|')]);



    if (questions.length === 0) {
        // Q6/empty: graceful, no stray CTA.
        return (
            <div className="max-w-3xl mx-auto text-center py-20">
                <div className="flex justify-center mb-4 text-book-accent/40"><IconQuestionLarge /></div>
                <p className="font-serif italic text-book-muted text-lg">No quiz questions available for this pack yet.</p>
            </div>
        );
    }

    const reset = () => { rawAnswers.current = {}; setIdx(0); setAnswer(null); setShowResult(false); setScore(0); setCompleted(false); };

    if (completed) {
        const total = questions.length;
        const msg = score === total ? "Perfect score — you're ready for the exam."
            : score >= total * 0.7 ? 'Strong understanding. Review what you missed and push for perceptive analysis.'
                : 'Keep studying. Review the Analysis section, then try again.';
        return (
            <div className="max-w-3xl mx-auto pb-8">
                {petReward && <p role="status" className="text-xs text-book-muted mb-3">{petReward}</p>}
                {syncStatus && <p role="status" className="text-xs text-book-muted mb-3">{syncStatus}{syncError && <button className="underline ml-2" onClick={syncReport}>Retry saving</button>}</p>}
                <div className="text-center border border-book-accent/30 bg-book-page/30 p-8 md:p-12 text-book-accent">
                    <div className="flex justify-center mb-6"><IconTrophy /></div>
                    <div className="font-display text-book-accent text-xs mb-3 font-semibold">Quiz Complete</div>
                    <h2 className="font-display text-4xl md:text-6xl text-book-text mb-4">{score} / {total}</h2>
                    <p className="font-serif italic text-book-muted text-lg mb-8">{msg}</p>
                    <button onClick={reset} className="inline-flex items-center gap-3 px-5 py-3 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors font-semibold">
                        <IconRefresh /> Try Again
                    </button>
                </div>
            </div>
        );
    }

    const q = questions[idx];
    const submit = () => {
        if (!isAnswerComplete(q, answer)) return;
        if (showResult) return;
        const nextScore = score + (isQuestionCorrect(q, answer) ? 1 : 0);
        setScore(nextScore); setShowResult(true);
        lastReport.current = { score: nextScore, total: questions.length, answered: idx + 1 };
        syncReport();
        if (q.id) rawAnswers.current[q.id] = answer;
        checkReward(Object.entries(rawAnswers.current).map(([id, answer]) => ({id, answer})));
    };
    const next = () => {
        if (idx < questions.length - 1) { setIdx((i) => i + 1); setAnswer(null); setShowResult(false); }
        else { setCompleted(true); }
    };

    return (
        <div className="max-w-3xl mx-auto pb-8">
            {petReward && <p role="status" className="text-xs text-book-muted mb-3">{petReward}</p>}
                {syncStatus && <p role="status" className="text-xs text-book-muted mb-3">{syncStatus}{syncError && <button className="underline ml-2" onClick={syncReport}>Retry saving</button>}</p>}
            <div className="mb-6 text-center">
                <div className="font-display text-book-accent text-xs mb-2 font-semibold">Quizzes</div>
                <h2 className="font-display text-2xl md:text-3xl text-book-text mb-2">{title}</h2>
            </div>

            {/* Progress */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="font-display text-book-accent text-xs font-semibold">Question {idx + 1} of {questions.length}</div>
                    <div className="font-display text-book-accent text-sm">Score: {score}</div>
                </div>
                <div className="h-1 bg-book-accent/10 rounded-full overflow-hidden">
                    <div className="h-full bg-book-accent transition-all duration-500" style={{ width: `${(idx / questions.length) * 100}%` }} />
                </div>
            </div>

            <div className="btl-quiz-card border border-book-accent/20 bg-book-page/30 p-5 md:p-7 mb-5 text-book-accent">
                <h3 className="font-display text-book-text text-base leading-relaxed mb-5">{q.question}</h3>
                <QuizQuestion q={q} answer={answer} setAnswer={setAnswer} submitted={showResult} />
            </div>

            <div className="flex justify-end">
                {!showResult ? (
                    <button onClick={submit} disabled={!isAnswerComplete(q, answer)}
                        className="px-5 py-3 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors disabled:opacity-30 disabled:cursor-not-allowed font-semibold">
                        Submit Answer
                    </button>
                ) : (
                    <button onClick={next}
                        className="flex items-center gap-3 px-5 py-3 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors font-semibold">
                        {idx < questions.length - 1 ? 'Next Question' : 'See Results'} <IconArrowRight />
                    </button>
                )}
            </div>

            {showCta && (
                <div className="mt-6 max-w-md mx-auto border border-book-accent/20 bg-book-bg/40 p-3 text-center">
                    <div className="font-display text-book-muted/80 text-xs mb-1 font-semibold">🔒 More with purchase</div>
                    <p className="font-serif italic text-book-muted/70 text-xs leading-snug font-semibold">More quizzes unlock with the pack.</p>
                </div>
            )}
        </div>
    );
}

function IconBookOpen() {
    return (<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 4h6a3 3 0 0 1 3 3v13a2.5 2.5 0 0 0-2.5-2.5H2zM22 4h-6a3 3 0 0 0-3 3v13a2.5 2.5 0 0 1 2.5-2.5H22z" /></svg>);
}
function IconPencil() {
    return (<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>);
}
function IconCards({ large }) {
    return (<svg className={large ? 'w-16 h-16' : 'w-3.5 h-3.5'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="14" height="12" rx="1.5" /><path d="M7 3h12a1.5 1.5 0 0 1 1.5 1.5V16" /></svg>);
}
function IconQuestion() {
    return (<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 .9-1 1.7" /><line x1="12" y1="17" x2="12" y2="17" /></svg>);
}
function IconRefresh() {
    return (<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v5h-5" /></svg>);
}
function IconQuestionLarge() {
    return (<svg className="w-16 h-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 .9-1 1.7" /><line x1="12" y1="17" x2="12" y2="17" /></svg>);
}
function IconTrophy() {
    return (<svg className="w-16 h-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0zM7 4H4v2a3 3 0 0 0 3 3M17 4h3v2a3 3 0 0 1-3 3" /></svg>);
}
function IconArrowRight() {
    return (<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>);
}

// Uses the same drill key and engine as the Guide's inline practice, never the
// pooled quiz key. Stored content/answers are passed through unchanged.
export function GuideQuizPanel({ guide, items, onReturn, ...progress }) {
    return <div className="max-w-4xl mx-auto">
        <button onClick={onReturn} className="mb-4 text-book-accent underline text-left break-words">← Return to {guide.content?.title || 'Essay Guide'}</button>
        <h2 className="font-display text-xl text-book-text mb-2">{guide.content?.title || 'Essay Guide'}</h2>
        <p className="text-sm text-book-muted mb-5">{toQuizQuestions(items).length} quizzes for this guide</p>
        <PracticeQuiz items={items} {...progress} />
        <button onClick={onReturn} className="mt-6 text-book-accent underline text-left break-words">← Return to {guide.content?.title || 'Essay Guide'}</button>
    </div>;
}
